/**
 * useFps — React hook that tracks current frame rate via requestAnimationFrame.
 *
 * Standalone substrate-agnostic FPS counter. Maintains its own rAF loop
 * (cheap — just a counter), exposes a smoothed rolling-window FPS that
 * updates ~every 500ms. Returns 0 in SSR / non-window environments.
 *
 * The canonical FPS source for debug overlays — gives the real browser-paint
 * cadence (no engine loop to read it off; the H10 boot host doesn't expose one).
 */

import { useEffect, useState } from 'react';

const UPDATE_INTERVAL_MS = 500;
const WINDOW_SIZE = 30;

export function useFps(): number {
  const [fps, setFps] = useState<number>(0);

  useEffect(() => {
    if (typeof requestAnimationFrame !== 'function') return;

    let rafId = 0;
    let cancelled = false;
    const frameTimes: number[] = [];
    let lastEmitMs = 0;

    const tick = (nowMs: number) => {
      if (cancelled) return;
      frameTimes.push(nowMs);
      if (frameTimes.length > WINDOW_SIZE) frameTimes.shift();

      if (nowMs - lastEmitMs >= UPDATE_INTERVAL_MS && frameTimes.length >= 2) {
        const span = frameTimes[frameTimes.length - 1] - frameTimes[0];
        const fpsRaw = span > 0 ? ((frameTimes.length - 1) * 1000) / span : 0;
        setFps(Math.round(fpsRaw));
        lastEmitMs = nowMs;
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return fps;
}
